/**
 * useAiChat — основной hook для AI-чата с SSE-стримингом.
 *
 * Контракт SSE-потока (см. docs/openapi.yaml `/api/ai/chat`):
 *   event: text
 *   data: {"delta": "..."}
 *
 *   event: tool_use_start
 *   data: {"id": "call_abc", "name": "get_member"}
 *
 *   event: tool_use_result
 *   data: {"id": "call_abc", "args_summary": "user_id=42"}
 *
 *   event: download
 *   data: {"export_id": "exp_xyz", "filename": "...", "rows": 120, "columns": 8}
 *
 *   event: error
 *   data: {"message": "..."}
 *
 *   event: final
 *   data: {}
 *
 * Парсер устойчив к:
 *  - чанкам, разрезающим SSE-сообщение посередине,
 *  - не-JSON `data:` (берётся как plain-text для `text`),
 *  - multi-line `data:` (объединяется через \n).
 *
 * Авторизация — `Authorization: Bearer <token>` из `tokenStore`. На 401
 * пробуем 1 раз сделать refresh и повторить запрос.
 */

import { useCallback, useRef } from 'react';
import { tokenStore, ApiError, apiFetch } from '@/api/client';
import { useAiStore, type AiAttachment, type AiMessage } from '@/state/aiStore';

interface SendOptions {
  attachments?: AiAttachment[];
  /** Текст, реально подмешанный в prompt (например, с содержимым файлов). */
  payloadOverride?: string;
}

interface ParsedSseEvent {
  event: string;
  data: string;
}

async function refreshOnce(): Promise<string | null> {
  try {
    const res = await fetch('/api/auth/refresh', {
      method: 'POST',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return null;
    const json = (await res.json()) as { access_token?: string };
    if (typeof json.access_token === 'string') {
      tokenStore.set(json.access_token);
      return json.access_token;
    }
    return null;
  } catch {
    return null;
  }
}

/** Парсит буфер из SSE-чанков и возвращает завершённые события + остаток. */
function parseSseBuffer(buffer: string): { events: ParsedSseEvent[]; rest: string } {
  const events: ParsedSseEvent[] = [];
  // SSE-события разделяются \n\n. Иногда сервер шлёт \r\n\r\n — нормализуем.
  const normalized = buffer.replace(/\r\n/g, '\n');
  const parts = normalized.split('\n\n');
  const rest = parts.pop() ?? '';

  for (const block of parts) {
    if (!block.trim()) continue;
    let event = 'message';
    const dataLines: string[] = [];
    for (const line of block.split('\n')) {
      if (line.startsWith(':')) continue; // comment
      if (line.startsWith('event:')) {
        event = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).replace(/^ /, ''));
      }
    }
    events.push({ event, data: dataLines.join('\n') });
  }

  return { events, rest };
}

function safeJson<T>(raw: string): T | null {
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function useAiChat() {
  const abortRef = useRef<AbortController | null>(null);

  const messages = useAiStore((s) => s.messages);
  const isStreaming = useAiStore((s) => s.isStreaming);
  const awaitingFirstChunk = useAiStore((s) => s.awaitingFirstChunk);

  // Извлекаем мутаторы из стора (стабильны по identity у Zustand).
  const appendUser = useAiStore((s) => s.appendUser);
  const startAssistant = useAiStore((s) => s.startAssistant);
  const appendText = useAiStore((s) => s.appendText);
  const startTool = useAiStore((s) => s.startTool);
  const finishTool = useAiStore((s) => s.finishTool);
  const addDownload = useAiStore((s) => s.addDownload);
  const addApproval = useAiStore((s) => s.addApproval);
  const setApprovalStatus = useAiStore((s) => s.setApprovalStatus);
  const setConversationId = useAiStore((s) => s.setConversationId);
  const setError = useAiStore((s) => s.setError);
  const finishStream = useAiStore((s) => s.finishStream);
  const reset = useAiStore((s) => s.reset);

  const sendMessage = useCallback(
    async (text: string, options: SendOptions = {}) => {
      const trimmed = text.trim();
      if (!trimmed && !options.attachments?.length) return;
      if (isStreaming) return;

      // 1. Регистрируем сообщение пользователя.
      const attachments = options.attachments;
      appendUser({ content: trimmed, attachments });

      // 2. Собираем payload для бэка. payloadOverride — то, что реально
      //    видит модель (например, с inline-содержимым прикреплённых файлов).
      const history: AiMessage[] = useAiStore.getState().messages;
      const apiMessages = history.map((m, idx) => {
        const isLastUser =
          idx === history.length - 1 && m.role === 'user';
        return {
          role: m.role,
          content: isLastUser && options.payloadOverride ? options.payloadOverride : m.content,
        };
      });

      // 3. Открываем assistant-сообщение.
      const assistantId = startAssistant();

      const controller = new AbortController();
      abortRef.current = controller;

      const doRequest = async (token: string | null): Promise<Response> => {
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
        };
        if (token) headers.Authorization = `Bearer ${token}`;
        return fetch('/api/ai/chat', {
          method: 'POST',
          headers,
          credentials: 'include',
          signal: controller.signal,
          body: JSON.stringify({
            messages: apiMessages,
            conversationId: useAiStore.getState().conversationId,
          }),
        });
      };

      try {
        let token = tokenStore.get();
        let response = await doRequest(token);

        if (response.status === 401) {
          token = await refreshOnce();
          if (!token) throw new ApiError(401, 'Сессия истекла', 'session_expired');
          response = await doRequest(token);
        }

        if (!response.ok || !response.body) {
          const text = response.headers.get('content-type')?.includes('application/json')
            ? ((await response.json()) as { detail?: string }).detail
            : await response.text();
          throw new ApiError(response.status, text ?? `HTTP ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = '';

        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const { events, rest } = parseSseBuffer(buffer);
          buffer = rest;

          for (const evt of events) {
            switch (evt.event) {
              case 'conversation': {
                const parsed = safeJson<{ id?: string }>(evt.data);
                if (parsed?.id) setConversationId(parsed.id);
                break;
              }
              case 'text': {
                // Backend шлёт {"content": "..."} согласно api/ai_chat.py:_sse_event.
                // Поддерживаем также legacy-варианты {delta, text} на случай рефакторинга.
                const parsed = safeJson<{ content?: string; delta?: string; text?: string }>(evt.data);
                const chunk = parsed?.content ?? parsed?.delta ?? parsed?.text ?? '';
                if (chunk) appendText(assistantId, chunk);
                break;
              }
              case 'tool_use_start': {
                const parsed = safeJson<{ id?: string; name?: string }>(evt.data);
                if (parsed?.name) {
                  startTool(
                    assistantId,
                    parsed.id ?? `tool-${Date.now()}`,
                    parsed.name,
                  );
                }
                break;
              }
              case 'tool_use_result': {
                const parsed = safeJson<{
                  id?: string;
                  args_summary?: string;
                  status?: 'done' | 'error';
                }>(evt.data);
                if (parsed?.id) {
                  finishTool(assistantId, parsed.id, {
                    argsSummary: parsed.args_summary,
                    status: parsed.status ?? 'done',
                  });
                }
                break;
              }
              case 'download': {
                const parsed = safeJson<{
                  export_id?: string;
                  filename?: string;
                  rows?: number;
                  columns?: number;
                }>(evt.data);
                if (parsed?.export_id && parsed.filename) {
                  addDownload(assistantId, {
                    export_id: parsed.export_id,
                    filename: parsed.filename,
                    rows: parsed.rows ?? 0,
                    columns: parsed.columns ?? 0,
                  });
                }
                break;
              }
              case 'approval_request': {
                const parsed = safeJson<{ id?: string; tool?: string; action?: string }>(evt.data);
                if (parsed?.id) {
                  addApproval(assistantId, {
                    id: parsed.id,
                    tool: parsed.tool ?? 'tool',
                    action: parsed.action ?? '',
                    status: 'pending',
                  });
                }
                break;
              }
              case 'approval_resolved': {
                const parsed = safeJson<{ id?: string; decision?: 'allow' | 'deny' }>(evt.data);
                if (parsed?.id) {
                  setApprovalStatus(parsed.id, parsed.decision === 'allow' ? 'approved' : 'denied');
                }
                break;
              }
              case 'error': {
                const parsed = safeJson<{ message?: string }>(evt.data);
                setError(assistantId, parsed?.message ?? evt.data ?? 'Ошибка стриминга');
                return;
              }
              case 'final': {
                finishStream(assistantId);
                return;
              }
              default:
                // ignore unknown events (forwards-compat)
                break;
            }
          }
        }
        finishStream(assistantId);
      } catch (err) {
        if ((err as Error).name === 'AbortError') {
          finishStream(assistantId);
          return;
        }
        const detail =
          err instanceof ApiError
            ? err.detail
            : err instanceof Error
              ? err.message
              : 'Неизвестная ошибка';
        setError(assistantId, detail);
      } finally {
        abortRef.current = null;
      }
    },
    [
      isStreaming,
      appendUser,
      startAssistant,
      appendText,
      startTool,
      finishTool,
      addDownload,
      addApproval,
      setApprovalStatus,
      setConversationId,
      setError,
      finishStream,
    ],
  );

  // Остановить ответ пользователем. Помечаем текущее стриминговое сообщение
  // «остановлено» (наработка сохраняется), затем рвём fetch — сервер ловит
  // закрытие сокета, abort'ит агента и персистит частичный ответ в историю.
  const abort = useCallback(() => {
    const sid = useAiStore.getState().streamingMessageId;
    if (sid) useAiStore.getState().stopStream(sid);
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  return {
    messages,
    isStreaming,
    awaitingFirstChunk,
    sendMessage,
    approveAction,
    reset,
    abort,
  };
}

/**
 * Подтвердить/отклонить write-операцию. Резолвит ожидающий `canUseTool` на бэке.
 * Standalone (не хук) — вызывается из карточки апрува в AiMessage без prop-drilling.
 */
export async function approveAction(
  toolUseId: string,
  decision: 'allow' | 'deny',
): Promise<void> {
  const { setApprovalStatus } = useAiStore.getState();
  // Оптимистично обновляем карточку; бэк также пришлёт approval_resolved.
  setApprovalStatus(toolUseId, decision === 'allow' ? 'approved' : 'denied');
  try {
    await apiFetch('/api/ai/approve', { method: 'POST', json: { toolUseId, decision } });
  } catch {
    setApprovalStatus(toolUseId, 'pending');
  }
}
